/**
 * DocumentTwinLinksContainer — binds the "Verknüpfte Objekte" panel to the
 * farm explorer's lens registry (FR-017.7).
 *
 * A twin object is reachable at `/farm?lense=<lens>&item=<id>` only if some
 * lens shows its entity type. The lens registry is the one place that knows
 * that, so the href is derived from it rather than from a guessed plural of
 * the type (`person` → `persons` holds, `crop_cycle` → `crop_cycles` holds,
 * `delivery_event` has no lens at all and must not become a dead link).
 */

import React from 'react';

import type { LineRow } from '../../components/twinLinks/lineRows';
import { lensIdByEntityType, linkTargetTypes } from '../../components/twinLinks/lensTargets';
import { useFarmLenses } from '../../hooks/useTwin';
import DocumentTwinLinksPanel from './DocumentTwinLinksPanel';
import { fetchLinkTargetSuggestions } from './linkTargetSuggestions';

// Re-exported for the callers and tests that import them from here.
export { lensIdByEntityType, linkTargetTypes };

export const DocumentTwinLinksContainer: React.FC<{
  documentId: string | null | undefined;
  /**
   * The document's extracted rows and date, for the line picker (FR-017 #5).
   * Passed in from the preview's metadata read rather than fetched here: the
   * page already holds it, and a second reader would be a second round trip.
   */
  lines?: readonly LineRow[];
  documentDate?: string | null;
}> = ({ documentId, lines, documentDate }) => {
  const { data: lenses } = useFarmLenses(Boolean(documentId));
  const byType = React.useMemo(() => lensIdByEntityType(lenses), [lenses]);
  const addTargets = React.useMemo(() => linkTargetTypes(lenses), [lenses]);

  const targetHref = React.useCallback(
    (entityType: string, entityId: string) => {
      const lensId = byType.get(entityType);
      if (!lensId) return null;
      const params = new URLSearchParams({ lense: lensId, item: entityId });
      return `/farm?${params.toString()}`;
    },
    [byType],
  );

  if (!documentId) return null;
  return (
    // Keyed by document: the preview's next/prev swaps `documentId` under a
    // mounted panel, and a target picked for one document must not be linked
    // to the next one (codex, #3 r1). A remount drops every piece of state.
    <DocumentTwinLinksPanel
      key={documentId}
      documentId={documentId}
      targetHref={targetHref}
      addTargets={addTargets}
      searchEntities={fetchLinkTargetSuggestions}
      lines={lines}
      documentDate={documentDate}
    />
  );
};

export default DocumentTwinLinksContainer;
